@use "styles/abstracts/variables" as v;
@use "styles/abstracts/mixins/common" as m;
@use "styles/abstracts/mixins/typography" as t;

.progress {
  // Default colors (for cases when shipmentType is not specified)
  $progress-active: v.$palette-gray-900;
  $progress-disabled: v.$palette-gray-200;
  $progress-disabled-dot: v.$palette-gray-300;

  @mixin point-disabled {
    background-color: $progress-disabled !important;

    &:after {
      background-color: $progress-disabled-dot !important;
    }
  }

  @mixin point-active {
    background-color: $progress-active;

    &:after {
      background-color: v.$palette-white;
    }
  }

  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  opacity: 0;
  @include m.appearance(0.4);

  .point {
    @include m.circle(12px, 4px);
    position: relative;
    z-index: v.$z-index-ui-progress-point;

    &.pointB {
      margin-left: auto;
      z-index: 0;
    }

    &.disabled {
      @include point-disabled;
    }
  }

  .progressLine {
    transition: all 0.4s;

    &.empty {
      position: absolute;
      content: "";
    }

    &.filled {
      background-color: $progress-active;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      left: 0;

      .pointCurrent {
        position: absolute !important;
        right: 0;
        z-index: v.$z-index-ui-progress-current-point;
        @include m.circle(16px, 12px);

        svg {
          z-index: 1;
          position: relative;
          height: 16px;
          width: 16px;

          path {
            fill: $progress-active;
            visibility: visible;
          }
        }
      }
    }
  }

  &.notFounded {
    .filled {
      display: none;
    }

    .point {
      @include point-disabled;
    }
  }

  &.location-data {
    .point {
      @include point-active;
    }
  }

  &.horizontal {
    width: 100%;
    height: 25.6px;
    flex-shrink: 0;

    .progressLine {
      height: 3px;

      &.empty {
        width: 100%;
        left: 0;
        @include m.dotted-border($progress-disabled-dot, 3px, horizontal);
      }

      &.filled {
        min-width: 48px;
      }
    }
  }

  &.vertical {
    flex-direction: column;
    width: 25.6px;

    .point {
      &.pointB {
        margin: 0;
        position: absolute;
        bottom: 0;
      }
    }

    .progressLine {
      width: 3px;

      &.empty {
        height: 100%;
        left: auto;
        @include m.dotted-border($progress-disabled-dot, 6.4px, vertical);
      }

      &.filled {
        display: flex;
        justify-content: center;
        align-items: center;

        .point {
          bottom: 0;
          right: auto;
          transform: rotate(90deg);
        }
      }
    }
  }

  // Shipment type specific colors
  &.sea {
    $progress-active: v.$palette-gray-900;
    $progress-disabled: v.$palette-gray-200;
    $progress-disabled-dot: v.$palette-gray-300;

    @mixin point-disabled {
      background-color: $progress-disabled !important;

      &:after {
        background-color: $progress-disabled-dot !important;
      }
    }

    @mixin point-active {
      background-color: $progress-active;

      &:after {
        background-color: v.$palette-white;
      }
    }

    .point {
      &.disabled {
        @include point-disabled;
      }
    }

    .progressLine {
      &.empty {
        &.horizontal {
          @include m.dotted-border($progress-disabled-dot, 3px, horizontal);
        }

        &.vertical {
          @include m.dotted-border($progress-disabled-dot, 6.4px, vertical);
        }
      }

      &.filled {
        background-color: $progress-active;

        .pointCurrent {
          svg path {
            fill: $progress-active;
          }
        }
      }
    }

    &.location-data {
      .point {
        @include point-active;
      }
    }
  }

  &.air {
    $progress-active: v.$palette-red;
    $progress-disabled: v.$palette-gray-200;
    $progress-disabled-dot: v.$palette-gray-300;

    @mixin point-disabled {
      background-color: $progress-disabled !important;

      &:after {
        background-color: $progress-disabled-dot !important;
      }
    }

    @mixin point-active {
      background-color: $progress-active;

      &:after {
        background-color: v.$palette-white;
      }
    }

    .point {
      &.disabled {
        @include point-disabled;
      }
    }

    .progressLine {
      &.empty {
        &.horizontal {
          @include m.dotted-border($progress-disabled-dot, 3px, horizontal);
        }

        &.vertical {
          @include m.dotted-border($progress-disabled-dot, 6.4px, vertical);
        }
      }

      &.filled {
        background-color: $progress-active;

        .pointCurrent {
          svg path {
            fill: $progress-active;
          }
        }
      }
    }

    &.location-data {
      .point {
        @include point-active;
      }
    }
  }

  &.rail {
    $progress-active: v.$palette-orange;
    $progress-disabled: v.$palette-gray-200;
    $progress-disabled-dot: v.$palette-gray-300;

    @mixin point-disabled {
      background-color: $progress-disabled !important;

      &:after {
        background-color: $progress-disabled-dot !important;
      }
    }

    @mixin point-active {
      background-color: $progress-active;

      &:after {
        background-color: v.$palette-white;
      }
    }

    .point {
      &.disabled {
        @include point-disabled;
      }
    }

    .progressLine {
      &.empty {
        &.horizontal {
          @include m.dotted-border($progress-disabled-dot, 3px, horizontal);
        }

        &.vertical {
          @include m.dotted-border($progress-disabled-dot, 6.4px, vertical);
        }
      }

      &.filled {
        background-color: $progress-active;

        .pointCurrent {
          svg path {
            fill: $progress-active;
          }
        }
      }
    }

    &.location-data {
      .point {
        @include point-active;
      }
    }
  }

  &.road {
    $progress-active: v.$palette-teal;
    $progress-disabled: v.$palette-gray-200;
    $progress-disabled-dot: v.$palette-gray-300;

    @mixin point-disabled {
      background-color: $progress-disabled !important;

      &:after {
        background-color: $progress-disabled-dot !important;
      }
    }

    @mixin point-active {
      background-color: $progress-active;

      &:after {
        background-color: v.$palette-white;
      }
    }

    .point {
      &.disabled {
        @include point-disabled;
      }
    }

    .progressLine {
      &.empty {
        &.horizontal {
          @include m.dotted-border($progress-disabled-dot, 3px, horizontal);
        }

        &.vertical {
          @include m.dotted-border($progress-disabled-dot, 6.4px, vertical);
        }
      }

      &.filled {
        background-color: $progress-active;

        .pointCurrent {
          svg path {
            fill: $progress-active;
          }
        }
      }
    }

    &.location-data {
      .point {
        @include point-active;
      }
    }
  }

  &.parcel {
    $progress-active: v.$palette-orange-light-2;
    $progress-disabled: v.$palette-gray-200;
    $progress-disabled-dot: v.$palette-gray-300;

    @mixin point-disabled {
      background-color: $progress-disabled !important;

      &:after {
        background-color: $progress-disabled-dot !important;
      }
    }

    @mixin point-active {
      background-color: $progress-active;

      &:after {
        background-color: v.$palette-white;
      }
    }

    .point {
      &.disabled {
        @include point-disabled;
      }
    }

    .progressLine {
      &.empty {
        &.horizontal {
          @include m.dotted-border($progress-disabled-dot, 3px, horizontal);
        }

        &.vertical {
          @include m.dotted-border($progress-disabled-dot, 6.4px, vertical);
        }
      }

      &.filled {
        background-color: $progress-active;

        .pointCurrent {
          svg path {
            fill: $progress-active;
          }
        }
      }
    }

    &.location-data {
      .point {
        @include point-active;
      }
    }
  }
}
